import MapClickHandler from 'components/BaseMap/components/MapClickHandler';
import MapToolsPanel from 'components/BaseMap/components/MapToolsPanel/MapToolsPanel';
import L from 'leaflet';
import { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Polygon, Tooltip } from 'react-leaflet';
import { useGetJurisdictionsQuery } from 'services/geography/geography.api';
import {
  DEFAULT_IPAWS_CIRCLE_RADIUS_KM,
  DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM,
  getIpawsAreaShapeBoundaryCoordinates,
  getIpawsPolygonCenter,
  getIpawsSquareCoordinates,
  IPAWS_AREA_TYPES,
  moveIpawsPolygon,
  transformIpawsPolygon,
} from '../../../../utils/ipawsArea.utils';
import useRescAlertAreaEditor from '../../hooks/useRescAlertAreaEditor';
import {
  getRescAlertOutsideCoordinates,
  RESCALERT_AREA_LABEL,
  validateJurisdictionBoundaries,
} from '../../utils/alertMapEditor.utils';
import { getOutsideAreaPathOptions, OUTSIDE_AREA_TOOLTIP } from '../../utils/outsideArea.utils';
import { getNearestPolygonEdgePoint } from '../../utils/polygonEditor.utils';
import AlertPointMarker from '../AlertPointMarker/AlertPointMarker';
import GeometryToolsPanel from '../GeometryToolsPanel/GeometryToolsPanel';
import OutsideAreaBadge from '../OutsideAreaBadge/OutsideAreaBadge';
import OutsideCoordinateMarkers from '../OutsideCoordinateMarkers/OutsideCoordinateMarkers';
import IpawsPlacementPreview from '../IpawsAreaMapContent/components/IpawsPlacementPreview/IpawsPlacementPreview';
import MapPointerTracker from '../IpawsAreaMapContent/components/MapPointerTracker/MapPointerTracker';
import ShapePlacementControls from '../ShapePlacementControls/ShapePlacementControls';

/** Draws and edits the original RescAlert polygon with the shared geometry controls. */
const RescAlertMapContent = ({ coordinates = [], currentMapCenter, severityColor }) => {
  const editor = useRescAlertAreaEditor(coordinates);
  const [dragPreviewCoordinates, setDragPreviewCoordinates] = useState(null);
  const displayedCoordinates = dragPreviewCoordinates ?? coordinates;
  const hasPolygon = coordinates.length >= 3;
  // The workplace jurisdictions, from the same query the form check and the point markers use.
  const { data: jurisdictions = [] } = useGetJurisdictionsQuery();
  // Recalculated on each change, so a mark goes away when its point is inside again.
  const outsidePoints = useMemo(
    () => getRescAlertOutsideCoordinates(displayedCoordinates, jurisdictions),
    [displayedCoordinates, jurisdictions],
  );
  const isAreaOutside = displayedCoordinates.length >= 3 && outsidePoints.length > 0;
  const areaPathOptions = {
    color: severityColor,
    fillColor: severityColor,
    fillOpacity: 0.28,
    opacity: 1,
    weight: 4,
    dashArray: '8 5',
  };
  const isDrawingPolygon = editor.activeTool === IPAWS_AREA_TYPES.POLYGON;
  const tracksPointer = [IPAWS_AREA_TYPES.SQUARE, IPAWS_AREA_TYPES.CIRCLE].includes(
    editor.activeTool,
  );
  const center = hasPolygon ? getIpawsPolygonCenter(coordinates) : null;
  const transformHandle = hasPolygon
    ? coordinates.reduce(
        (furthest, coordinate) =>
          Math.hypot(coordinate.lat - center.lat, coordinate.lng - center.lng) >
          Math.hypot(furthest.lat - center.lat, furthest.lng - center.lng)
            ? coordinate
            : furthest,
        coordinates[0],
      )
    : null;

  const getPlacedCoordinates = (position) => {
    if (editor.activeTool === IPAWS_AREA_TYPES.SQUARE) {
      return getIpawsSquareCoordinates(position, DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM);
    }
    if (editor.activeTool === IPAWS_AREA_TYPES.CIRCLE) {
      // RescAlert stores one polygon boundary, so sample the native circle into 32 editable points.
      return getIpawsAreaShapeBoundaryCoordinates({
        type: IPAWS_AREA_TYPES.CIRCLE,
        center: position,
        radiusKm: DEFAULT_IPAWS_CIRCLE_RADIUS_KM,
      }).filter((_coordinate, index) => index % 4 === 0);
    }
    return null;
  };

  const placeShape = (position) => {
    if (!position || !editor.activeTool) return;
    if (isDrawingPolygon) {
      editor.save([...coordinates, position]);
      return;
    }
    const placedCoordinates = getPlacedCoordinates(position);
    if (placedCoordinates?.length && editor.save(placedCoordinates)) editor.finishTool();
  };

  const placementPreviewShape = editor.pointerLocation
    ? {
        type: editor.activeTool,
        center: editor.pointerLocation,
        radiusKm: DEFAULT_IPAWS_CIRCLE_RADIUS_KM,
        sideLengthKm: DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM,
      }
    : null;
  // The outline turns the danger color while a click would place points outside the jurisdiction.
  const isPlacementPreviewOutside = placementPreviewShape
    ? !validateJurisdictionBoundaries(getPlacedCoordinates(editor.pointerLocation), jurisdictions)
    : false;

  const cancelPlacement = () => {
    if (isDrawingPolygon && coordinates.length) editor.save([]);
    editor.finishTool();
  };

  const startEditing = (editMode) => {
    // A valid in-progress polygon can move directly into editing without requiring a separate
    // Finish click. The Finish button remains available for operators who prefer that workflow.
    if (isDrawingPolygon && hasPolygon) editor.finishTool();
    editor.setEditMode(editMode);
  };

  const handlePolygonClick = (event) => {
    if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
    if (editor.editMode !== 'addVertex') return;
    const insertion = getNearestPolygonEdgePoint(coordinates, event?.latlng, {
      maximumDistanceMeters: Number.POSITIVE_INFINITY,
    });
    if (!insertion) return;
    const nextCoordinates = [...coordinates];
    nextCoordinates.splice(insertion.insertIndex, 0, insertion.position);
    editor.save(nextCoordinates);
  };

  const moveVertex = (vertexIndex, position) => {
    const nextCoordinates = [...coordinates];
    nextCoordinates[vertexIndex] = position;
    setDragPreviewCoordinates(null);
    return editor.save(nextCoordinates);
  };

  const previewVertexMove = (vertexIndex, position) => {
    const nextCoordinates = [...coordinates];
    nextCoordinates[vertexIndex] = position;
    setDragPreviewCoordinates(nextCoordinates);
  };

  const getTransformedCoordinates = (position) =>
    isTransformMode
      ? transformIpawsPolygon(coordinates, position, editor.editMode)
      : moveIpawsPolygon(coordinates, position);

  const previewShapeMove = (_index, position) => {
    setDragPreviewCoordinates(getTransformedCoordinates(position));
  };

  const commitShapeMove = (_index, position) => {
    const nextCoordinates = getTransformedCoordinates(position);
    setDragPreviewCoordinates(null);
    return editor.save(nextCoordinates);
  };

  const removeVertex = (vertexIndex) => {
    if (coordinates.length <= 3) return;
    editor.save(coordinates.filter((_coordinate, index) => index !== vertexIndex));
  };

  const isVertexMode = ['reshape', 'addVertex', 'eraseVertex'].includes(editor.editMode);
  const isTransformMode = ['resize', 'rotate'].includes(editor.editMode);

  return (
    <>
      <MapClickHandler onClick={(event) => placeShape(event.latlng)} />
      {tracksPointer ? <MapPointerTracker onPointerChange={editor.setPointerLocation} /> : null}

      <Polygon
        // Leaflet applies the outside class only when it creates the path, so a change remounts it.
        key={isAreaOutside ? 'outside' : 'inside'}
        pathOptions={isAreaOutside ? getOutsideAreaPathOptions(areaPathOptions) : areaPathOptions}
        positions={displayedCoordinates}
        eventHandlers={{ click: handlePolygonClick }}
        interactive={!isDrawingPolygon}
      >
        {isAreaOutside ? <Tooltip sticky>{OUTSIDE_AREA_TOOLTIP}</Tooltip> : null}
      </Polygon>
      {!editor.activeTool && editor.editMode === 'addVertex' ? (
        <Polygon
          positions={displayedCoordinates}
          pathOptions={{ color: severityColor, opacity: 0.001, weight: 24, fill: false }}
          eventHandlers={{ click: handlePolygonClick }}
        />
      ) : null}
      <IpawsPlacementPreview
        shape={placementPreviewShape}
        severityColor={severityColor}
        isOutside={isPlacementPreviewOutside}
      />
      {!editor.activeTool &&
        isVertexMode &&
        coordinates.map((position, index) => (
          <AlertPointMarker
            key={`${editor.editMode}-${index}`}
            position={position}
            markerIndex={index}
            accessibleName={`${editor.editMode === 'eraseVertex' ? 'Remove' : 'Move'} alert boundary point ${index + 1}`}
            draggable={editor.editMode === 'reshape'}
            onClick={editor.editMode === 'eraseVertex' ? () => removeVertex(index) : undefined}
            onMarkerMovePreview={editor.editMode === 'reshape' ? previewVertexMove : undefined}
            onMarkerMove={editor.editMode === 'reshape' ? moveVertex : undefined}
          />
        ))}
      {hasPolygon && !editor.activeTool && !isVertexMode ? (
        <AlertPointMarker
          position={isTransformMode ? transformHandle : center}
          markerIndex={0}
          accessibleName={`${editor.editMode === 'move' ? 'Move' : editor.editMode === 'rotate' ? 'Rotate' : 'Resize'} alert boundary`}
          showWorkplaceJurisdictionWarning={false}
          onMarkerMovePreview={previewShapeMove}
          onMarkerMove={commitShapeMove}
        />
      ) : null}
      {/* Last, so each mark sits above the area and the edit handles. */}
      <OutsideCoordinateMarkers points={outsidePoints} />

      <MapToolsPanel position="topright" width={280} header="RescAlert Alerting Tools" collapsible>
        <div className="alert-creator-area-tools d-flex flex-column gap-2">
          <ShapePlacementControls
            activeTool={editor.activeTool}
            canDrawPolygon={!coordinates.length}
            canPlaceSquare={!coordinates.length}
            canPlaceCircle={!coordinates.length}
            onStartTool={editor.startTool}
          />

          <small className="alert-creator-area-tool-status" role="status" aria-live="polite">
            {editor.activeTool === IPAWS_AREA_TYPES.POLYGON
              ? 'Click the map or add the map-center point for each polygon vertex.'
              : editor.activeTool === IPAWS_AREA_TYPES.SQUARE
                ? 'Click the map or use its current center to place a square.'
                : editor.activeTool === IPAWS_AREA_TYPES.CIRCLE
                  ? 'Click the map or use its current center to place a circle.'
                  : coordinates.length
                    ? 'Select an editing tool or clear the shape to draw another.'
                    : 'Choose a shape, then place it on the map.'}
          </small>

          {isAreaOutside ? (
            <div className="d-flex align-items-center gap-1 small">
              <span>{RESCALERT_AREA_LABEL}</span>
              <OutsideAreaBadge shapeLabel={RESCALERT_AREA_LABEL} />
            </div>
          ) : null}

          {editor.activeTool ? (
            <div className="d-flex flex-column gap-1">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="map-tools-button"
                onClick={() => placeShape(currentMapCenter)}
              >
                {isDrawingPolygon ? 'Add map-center vertex' : 'Place at map center'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="map-tools-button"
                onClick={
                  isDrawingPolygon && coordinates.length >= 3 ? editor.finishTool : cancelPlacement
                }
              >
                {isDrawingPolygon
                  ? coordinates.length >= 3
                    ? 'Finish polygon'
                    : 'Cancel polygon'
                  : 'Done placing'}
              </Button>
            </div>
          ) : null}

          {hasPolygon && (!editor.activeTool || isDrawingPolygon) ? (
            <>
              <GeometryToolsPanel
                activeMode={isDrawingPolygon ? null : editor.editMode}
                onSetMode={startEditing}
                onUndo={editor.undo}
                onRedo={editor.redo}
                onCancel={editor.cancel}
                canEraseVertex={coordinates.length > 3}
                canUndo={editor.canUndo}
                canRedo={editor.canRedo}
              />
              <small className="alert-creator-area-tool-status d-block mb-2">
                {editor.editMode === 'addVertex'
                  ? 'Click near the polygon border to add a vertex at the nearest edge point.'
                  : editor.editMode === 'eraseVertex'
                    ? 'Click a polygon vertex to remove it.'
                    : `${coordinates.length} drawn vertices`}
              </small>
            </>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="map-tools-button alert-creator-area-clear"
            disabled={!coordinates.length}
            onClick={() => editor.save([])}
          >
            Clear all shapes
          </Button>
        </div>
      </MapToolsPanel>
    </>
  );
};

export default RescAlertMapContent;
